/**
 * The onboarding interview: a few questions, one at a time, before the first
 * chat. What the person says becomes their Profile and Preferences in memory.
 *
 * Not in the prototype. Drawn in its language: one region on the ground, the
 * wordmark at the top with Skip opposite, a Baloo greeting, then the interview
 * as a chat — the assistant's text open on the panel, the person's answers in
 * cards — over a composer cut down to a text box and send. When the Worker
 * says the interview is over (`finished`), the composer gives way to the one
 * button that leaves.
 *
 * The stream is read here rather than through the turn store: there is no
 * chat to come back to, and the gate replaces this page the moment it ends.
 * A ref keeps the first question from being asked twice under StrictMode.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import {
  ApiError,
  finishOnboarding,
  getOnboarding,
  streamTurn,
  type Message
} from '../api';
import { Markdown } from '../components/Markdown';
import { ThinkingLine, UserMessage } from '../components/MessageList';
import { Ph } from '../components/ui';
import { ds } from '../ds';
import { useStickToBottom } from '../hooks/useStickToBottom';
import { localUserMessage } from '../hooks/useTurns';
import { useViewport } from '../hooks/useViewport';
import { useApp } from '../state';

interface Failure {
  message: string;
  retry: () => void;
}

const failureText = (err: unknown): string =>
  err instanceof ApiError && err.status !== 0 ? err.message : 'Assist did not answer.';

export function OnboardingPage() {
  const { me, refreshMe } = useApp();
  const { narrow } = useViewport();
  const { Wordmark, Button } = ds();

  const [messages, setMessages] = useState<Message[] | null>(null);
  const [live, setLive] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [leaving, setLeaving] = useState<'finish' | 'skip' | null>(null);
  const [draft, setDraft] = useState('');
  const input = useRef<HTMLTextAreaElement>(null);
  const started = useRef(false);
  const { scrollRef, contentRef, stick } = useStickToBottom();

  const turn = useCallback(
    (text: string) => {
      setFailure(null);
      const local = text ? localUserMessage({ text, attachments: [] }) : null;
      if (local) setMessages((m) => [...(m ?? []), local]);
      setLive('');
      stick();

      let got = '';
      let settled = false;
      let stored = false;
      const fail = (message: string) => {
        settled = true;
        setLive(null);
        setFailure({
          message,
          retry: () => {
            // An answer the Worker stored is not sent again: an empty call
            // asks again. One it never stored is taken out and resent, so it
            // does not show twice.
            if (stored) return turn('');
            if (local) setMessages((m) => (m ?? []).filter((x) => x.id !== local.id));
            turn(text);
          }
        });
      };

      streamTurn('/onboarding/turn', { text }, (e) => {
        switch (e.type) {
          case 'user':
            stored = true;
            if (local) setMessages((m) => (m ?? []).map((x) => (x.id === local.id ? e.message : x)));
            break;
          case 'token':
            got += e.text;
            setLive(got);
            break;
          case 'done':
            settled = true;
            setMessages((m) => [...(m ?? []), e.message]);
            setLive(null);
            break;
          case 'finished':
            setFinished(true);
            break;
          case 'error':
            if (e.code === 'ai_disabled') void refreshMe();
            fail(e.message);
            break;
          default:
            break;
        }
      })
        .then(() => {
          if (!settled) fail('The reply did not finish.');
        })
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.code === 'ai_disabled') void refreshMe();
          fail(failureText(err));
        });
    },
    [refreshMe, stick]
  );

  const load = useCallback(() => {
    setFailure(null);
    getOnboarding()
      .then((state) => {
        setMessages(state.messages);
        setFinished(state.finished);
        // Nothing asked yet, or an answer still waiting for its reply: an
        // empty turn asks.
        if (!state.finished && (!state.messages.length || state.messages[state.messages.length - 1]!.role === 'user')) {
          turn('');
        }
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.code === 'ai_disabled') void refreshMe();
        setMessages([]);
        setFailure({ message: failureText(err), retry: load });
      });
  }, [refreshMe, turn]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    load();
  }, [load]);

  useLayoutEffect(() => {
    const el = input.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [draft]);

  const leave = async (skip: boolean) => {
    setLeaving(skip ? 'skip' : 'finish');
    setFailure(null);
    try {
      await finishOnboarding({ skip });
      await refreshMe();
    } catch (err) {
      setLeaving(null);
      setFailure({ message: failureText(err), retry: () => void leave(skip) });
    }
  };

  const busy = live !== null;
  const canSend = !!draft.trim() && !busy && !finished && messages !== null;

  const send = () => {
    if (!canSend) return;
    const text = draft.trim();
    setDraft('');
    turn(text);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Enter' && !e.shiftKey && !window.matchMedia?.('(pointer: coarse)').matches) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div
      className="mv-app"
      style={{
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        justifyContent: 'center',
        padding: narrow ? 0 : 16,
        background: 'var(--app-ground)',
        color: 'var(--text-body)',
        fontFamily: 'var(--font-body)'
      }}
    >
      <main
        style={{
          width: '100%',
          maxWidth: 720,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--app-panel)',
          borderRadius: narrow ? 0 : 'var(--radius-region)',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            flex: 'none',
            height: 52,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 12px 0 20px'
          }}
        >
          <Wordmark lockup="ink" word="assist" badge="sparkle" size={17} />
          <Button
            variant="ghost"
            density="app"
            size="sm"
            disabled={leaving !== null}
            onClick={() => void leave(true)}
          >
            Skip
          </Button>
        </div>

        <div ref={scrollRef} className="scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <div
            ref={contentRef}
            style={{
              padding: narrow ? '20px 16px 24px' : '28px 32px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: 16
            }}
          >
            <h1
              style={{
                margin: '0 0 8px',
                fontFamily: 'var(--font-display)',
                fontWeight: 800,
                fontSize: narrow ? 34 : 40,
                lineHeight: 1.02,
                letterSpacing: 'normal',
                color: 'var(--text-strong)'
              }}
            >
              Hello{me?.user.firstName ? `, ${me.user.firstName}` : ''}.
            </h1>

            {(messages ?? []).map((m) =>
              m.role === 'user' ? (
                <UserMessage key={m.id} message={m} />
              ) : m.body.kind === 'text' ? (
                <Markdown key={m.id} text={m.body.text} />
              ) : null
            )}

            {live !== null ? live ? <Markdown text={live} /> : <ThinkingLine /> : null}

            {failure ? (
              <div
                role="alert"
                style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: 'var(--text-muted)' }}
              >
                <Ph name="warning-circle" size={15} color="var(--danger-500)" />
                <span>{failure.message}</span>
                <button
                  type="button"
                  className="bare"
                  onClick={failure.retry}
                  style={{ fontSize: 13.5, color: 'var(--text-link)' }}
                >
                  Retry
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <div style={{ flex: 'none', padding: narrow ? '8px 12px 16px' : '8px 32px 24px' }}>
          {finished ? (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="primary"
                density="app"
                size="md"
                disabled={leaving !== null}
                onClick={() => void leave(false)}
              >
                Start using Assist.
              </Button>
            </div>
          ) : (
            <div
              style={{
                background: 'var(--surface-card)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-sm)',
                padding: 8,
                display: 'flex',
                alignItems: 'flex-end',
                gap: 8
              }}
            >
              <textarea
                ref={input}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Reply"
                aria-label="Reply"
                rows={1}
                autoFocus={!narrow}
                style={{
                  flex: 1,
                  minWidth: 0,
                  border: 0,
                  background: 'transparent',
                  resize: 'none',
                  padding: '8px 8px',
                  fontSize: 15,
                  lineHeight: 1.5,
                  color: 'var(--text-strong)',
                  boxShadow: 'none'
                }}
              />
              <button
                type="button"
                className="bare"
                aria-label="Send"
                title="Send"
                disabled={!canSend}
                onClick={send}
                style={{
                  width: 36,
                  height: 36,
                  flex: 'none',
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: 'var(--radius-sm)',
                  background: draft.trim() ? 'var(--primary)' : 'var(--app-panel-sunken)',
                  color: draft.trim() ? 'var(--text-on-primary)' : 'var(--text-faint)',
                  cursor: canSend ? 'pointer' : 'default'
                }}
              >
                <Ph name="paper-plane-tilt" size={17} />
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
